/**
 * Administration › Droits et groupes (administrateurs, lecture seule) : qui a accès à quoi, qui est dans quel groupe.
 *  - « Droits »  : matrice élément du catalogue × groupe (actions autorisées, calculées par Verified Permissions pour un
 *                  membre de ce seul groupe ; « Tout utilisateur » = droits communs, retirés des colonnes de groupe) ;
 *  - « Membres » : membres de chaque groupe (miroir Cognito des groupes Entra, à jour après la synchronisation).
 * Recherche : une personne → ses groupes seulement, plus son accès (union de ses groupes) ; un groupe → ce groupe.
 * Les groupes se gèrent dans Entra ID, les règles dans infra/authz (historique : docs/DROITS.md).
 * API : GET /v1/admin/droits.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ACTIONS_NOEUD, cleLibre, noeuds, type Action, type DroitsAdministration, type DroitsGroupe, type MembreGroupe } from '@agora/shared';
import { DataTable, Icon, RecordSkeleton, SegmentedControl, StatusBadge, TextField, cx } from '../../design';
import { ErreurApi, useApi, useFormats, useMoi, useT } from '../../socle';
import { NonAutorise } from './PageNoeud';

type Vue = 'droits' | 'membres';
const COMMUN = '';
const NIVEAU = { Metier: 1, Module: 2, SousModule: 3, Dimension: 1 } as const;

/** Nom de colonne compact : sans le préfixe commun « AGORA- ». */
const court = (groupe: string) => groupe.replace(/^AGORA-/, '');

export function AdministrationDroits() {
  const { t, lib } = useT();
  const moi = useMoi();
  const api = useApi();
  const { dateHeure } = useFormats();
  const [vue, setVue] = React.useState<Vue>('droits');
  const [recherche, setRecherche] = React.useState('');

  const requete = useQuery({ queryKey: ['admin', 'droits'], queryFn: () => api.get<DroitsAdministration>('/v1/admin/droits'), enabled: moi.admin });

  if (!moi.admin) return <NonAutorise titre={t('admin.droits.titre')} />;
  const donnees = requete.data;

  /** Actions en clair, regroupées quand c'est possible (« Tout », « Tout sauf administrer », « Tout sauf approuver et administrer »). */
  const enClair = (actions: Action[] | undefined) => {
    if (!actions?.length) return '';
    const propres = ACTIONS_NOEUD.filter((a) => actions.includes(a));
    const manquantes = ACTIONS_NOEUD.filter((a) => !propres.includes(a)).sort().join(',');
    const accueil = actions.includes('voirAccueil') ? [t('admin.droits.voirAccueil')] : [];
    if (!manquantes) return [t('admin.droits.tout'), ...accueil].join(', ');
    if (manquantes === 'administrer') return [t('admin.droits.toutSaufAdministrer'), ...accueil].join(', ');
    if (manquantes === 'administrer,approuver') return [t('admin.droits.toutSaufApprouverAdministrer'), ...accueil].join(', ');
    return [...accueil, ...propres.map((a) => t(cleLibre('droits', a)))].join(', ');
  };

  // Recherche : personnes (nom, e-mail) puis groupes (nom).
  const q = recherche.trim().toLowerCase();
  const personnes = new Map<string, MembreGroupe>();
  for (const g of donnees?.groupes ?? []) for (const m of g.membres) personnes.set(m.email, m);
  const trouvees = q ? [...personnes.values()].filter((m) => m.nom.toLowerCase().includes(q) || m.email.includes(q)) : [];
  const groupes = (donnees?.groupes ?? []).filter((g) =>
    !q || g.groupe.toLowerCase().includes(q) || g.membres.some((m) => trouvees.some((p) => p.email === m.email)));
  const personne = trouvees.length === 1 ? trouvees[0] : undefined;
  const groupesDe = (email: string) => (donnees?.groupes ?? []).filter((g) => g.membres.some((m) => m.email === email));

  const actionsDe = (cle: string, id: string): Action[] | undefined => {
    if (!donnees) return undefined;
    if (cle === COMMUN) return donnees.commun[id];
    if (personne && cle === `@${personne.email}`) {
      const union = new Set<Action>([...(donnees.commun[id] ?? []), ...groupesDe(personne.email).flatMap((g) => g.droits[id] ?? [])]);
      return [...union];
    }
    return groupes.find((g) => g.groupe === cle)?.droits[id];
  };
  // Matrice : une colonne par groupe ayant des droits propres ; les autres (accès, groupes de lecture) sont listés à part.
  const avecDroits = groupes.filter((g) => Object.keys(g.droits).length);
  const sansDroit = groupes.filter((g) => !Object.keys(g.droits).length);
  const colonnes = [
    { key: COMMUN, label: t('admin.droits.commun') },
    ...avecDroits.map((g) => ({ key: g.groupe, label: court(g.groupe) })),
    ...(personne ? [{ key: `@${personne.email}`, label: t('admin.droits.acces', { nom: personne.nom }) }] : []),
  ];
  const lignes = noeuds()
    .filter((n) => colonnes.some((c) => actionsDe(c.key, n.id)?.length))
    .map((n) => ({
      id: n.id,
      element: <span className={cx('agx-droits-element', `agx-niveau-${n.metier ? NIVEAU[n.type] : 1}`)}>{lib(n.id)}</span>,
      ...Object.fromEntries(colonnes.map((c) => [`c${c.key}`, enClair(actionsDe(c.key, n.id))])),
    }));

  const filiales = (g: DroitsGroupe) => (g.filiales.length > 1 ? t('admin.droits.toutesFiliales') : g.filiales.join(', ') || t('admin.droits.aucuneFiliale'));
  const membresAffiches = (g: DroitsGroupe) => (trouvees.length ? g.membres.filter((m) => trouvees.some((p) => p.email === m.email)) : g.membres);

  return (
    <>
      <div className="ag-pagehead">
        <div>
          <h1>{t('admin.droits.titre')}</h1>
          <p className="agx-sous-titre">{t('admin.droits.sousTitre')}</p>
        </div>
      </div>

      <section className="ag-glass-card agx-carte" aria-label={t('admin.droits.affichage')}>
        <div className="agx-droits-outils">
          <SegmentedControl ariaLabel={t('admin.droits.affichage')} value={vue} onChange={(v) => setVue(v as Vue)}
            options={[{ value: 'droits', label: t('admin.droits.vueDroits') }, { value: 'membres', label: t('admin.droits.vueMembres') }]} />
          <div className="agx-droits-recherche">
            <TextField label={t('admin.droits.recherche')} value={recherche} onChange={setRecherche} autoComplete="off" />
          </div>
        </div>
        <div className="agx-ligne-badges">
          {donnees ? <StatusBadge status="info" label={t('admin.droits.compte', { groupes: donnees.groupes.length, personnes: personnes.size })} /> : null}
          {donnees ? <StatusBadge status="neutral" label={t('admin.droits.calculeLe', { date: dateHeure(donnees.calculeLe) })} /> : null}
          {personne ? (
            <StatusBadge status="success" label={t('admin.droits.groupesDe', { nom: personne.nom, groupes: groupesDe(personne.email).map((g) => court(g.groupe)).join(', ') })} />
          ) : null}
        </div>
        <p className="agx-vide">{t('admin.droits.source')}</p>
      </section>

      {/* Chargement : squelette de la vue affichée (matrice des droits ou cartes des groupes). */}
      {requete.isLoading && vue === 'droits' ? (
        <div className="agx-droits-matrice">
          <DataTable dense ariaLabel={t('admin.droits.vueDroits')} loading loadingLabel={t('app.chargement')}
            columns={[{ key: 'element', label: t('admin.droits.element'), width: 240 }, { key: 'commun', label: t('admin.droits.commun') }]}
            rows={[]} />
        </div>
      ) : null}
      {requete.isLoading && vue === 'membres' ? <RecordSkeleton label={t('app.chargement')} header={false} cards={3} fields={3} /> : null}
      {requete.error ? (
        <div className="agx-message" role="alert"><Icon name="bell" size={16} /><span>{t(`erreurs.${requete.error instanceof ErreurApi ? requete.error.code : 'interne'}`)}</span></div>
      ) : null}
      {donnees && q && !groupes.length ? <p className="agx-vide" role="status">{t('admin.droits.aucunResultat')}</p> : null}

      {donnees && vue === 'droits' && sansDroit.length ? (
        <p className="agx-vide">{t('admin.droits.groupesSansDroit', { groupes: sansDroit.map((g) => court(g.groupe)).join(', ') })}</p>
      ) : null}
      {donnees && vue === 'droits' ? (
        <div className="agx-droits-matrice">
          <DataTable dense ariaLabel={t('admin.droits.vueDroits')} emptyLabel={t('admin.droits.aucunDroit')}
            columns={[{ key: 'element', label: t('admin.droits.element'), width: 240 }, ...colonnes.map((c) => ({ key: `c${c.key}`, label: c.label }))]}
            rows={lignes} />
        </div>
      ) : null}

      {donnees && vue === 'membres' ? (
        <div className="agx-grille agx-droits-membres">
          {groupes.map((g) => (
            <section key={g.groupe} className="ag-glass-card agx-carte" aria-label={g.groupe}>
              <h2 className="agx-titre-carte ag-mono">{g.groupe}</h2>
              <div className="agx-ligne-badges">
                <StatusBadge status="neutral" label={filiales(g)} />
                <StatusBadge status="info" label={t('admin.droits.nbMembres', { n: g.membres.length })} />
                {!Object.keys(g.droits).length ? <StatusBadge status="neutral" label={t('admin.droits.sansDroitPropre')} /> : null}
              </div>
              <DataTable dense ariaLabel={g.groupe} emptyLabel={t('admin.droits.aucunMembre')}
                columns={[{ key: 'nom', label: t('admin.droits.nom') }, { key: 'email', label: t('admin.droits.email') }, { key: 'etat', label: t('admin.droits.etat'), width: 110 }]}
                rows={membresAffiches(g).map((m) => ({
                  id: m.email,
                  nom: m.nom,
                  email: m.email,
                  etat: <StatusBadge status={m.actif ? 'success' : 'neutral'} label={m.actif ? t('admin.droits.actif') : t('admin.droits.desactive')} />,
                }))} />
            </section>
          ))}
        </div>
      ) : null}
    </>
  );
}
